import {
    createAsyncThunk,
    createSlice,
    isRejected
} from '@reduxjs/toolkit';
import { httpGet, httpPost } from '../../helpers/http.js';
import { launchTypes } from './helpers.js';
import { buildYouTubeEmbedUrl } from './youtube.js';
import { buildVimeoEmbedUrl } from './vimeo.js';

export const launchElearning = createAsyncThunk(
    'elearning/launch',
    async (args, { getState }) => {
        const { elearning, env, auth } = getState();
        const { playerType, launchId, launchType, progress } = args.item;
        const { user } = auth;
        const isNewOrResetted = !progress;

        let url = null,
            launchData = null,
            playerLaunchType = launchTypes.LAUNCHTYPE_REGULAR;

        if (playerType === undefined || playerType === null || playerType === 'LTI1_TCG') {
            // fallback for legacy tcg lti items
            const ltiData = await httpPost(`${env.settings.ltiConsumerApi}/api/1.0/tcgStartUrl`, user, {
                uniqueId: launchId,
                launchPresentationReturnUrl: getReturnUrl(launchType, true),
                reset: isNewOrResetted, // tcgStartUrl only
                displayName: user.profile.name,
                email: user.profile.email,
            });
            url = ltiData.url;
        } else {
            const command = env.preview ? 'preview' : isNewOrResetted ? 'reset-start' : 'start';
            const data = await httpGet(`${env.settings.compositionApi}/elearning/${launchId}/${command}`, user);

            playerLaunchType = data.launchType || launchTypes.LAUNCHTYPE_REGULAR;

            switch (playerType) { // fix case types when we're sending enums as strings
                case 0: // 'lti1':
                    const ltiData = await httpPost(data.startUrl, user, {
                        uniqueId: launchId,
                        launchPresentationReturnUrl: getReturnUrl(launchType, false),
                        instanceId: data.session.referenceId,
                        idString: user.profile.sub, // startUrl only
                        displayName: user.profile.name,
                        email: user.profile.email,
                    });
                    url = ltiData.url;

                    // override the launchType for lti1 items to match the newWindow value from the ltiData response
                    playerLaunchType = ltiData.newWindow === true
                        ? launchTypes.LAUNCHTYPE_NEWWINDOW
                        : launchTypes.LAUNCHTYPE_REGULAR;
                    break;

                case 3: // 'lti-advantage'
                    const response = await httpPost(`${data.startData.launchTokenUrl}/launchtoken`, user, {
                        portalIdentifier: env.portalIdentifier,
                        launchId: data.startData.resourceLink,
                        userName: user.profile.name,
                        userEmail: user.profile.email,
                    });
                    url = response.launchUrl;
                    break;

                case 4: // 'youtube':
                    url = buildYouTubeEmbedUrl(data.startData);
                    break;

                case 5: // 'vimeo':
                    url = buildVimeoEmbedUrl(data.startData);
                    break;

                case 1: // 'onlineStudio':
                case 2: // 'scorm12':
                case 99: // 'blackbox':
                default:
                    url = data.startUrl;
                    break;
            }

            const { sessionStatus, referenceId }  = data.session || {};

            launchData = {
                ...(data.startData || {}),
                referenceId: referenceId,
            };

            if (data.autoComplete && sessionStatus !== 'completed') {
                await httpPost(`${env.settings.compositionApi}/elearning/status/`, auth.user, {
                    targetId: launchId,
                    id: referenceId,
                    completionStatus: 'completed',
                });
            }
        }

        switch (launchType) {
            case 'popup':
                return {
                    playerType: playerType,
                    launchId: launchId,
                    launchType: playerLaunchType,
                    launchData: launchData,
                    url: url,
                    popupCanClose: true,
                    popup: true,
                };

            case 'popup-close':
                return {
                    playerType: playerType,
                    launchId: launchId,
                    launchType: playerLaunchType,
                    launchData: launchData,
                    url: url,
                    popupCanClose: false,
                    popup: true,
                };

            case 'window':
                window.open(url, '_blank');
                return {
                    launchId: launchId,
                    launchType: playerLaunchType,
                    popup: false,
                };

            case 'self':
            default:
                window.location.href = url;
                return {
                    launchId: launchId,
                    url: url,
                    popup: false,
                };
        }
    },
);

export const closeElearning = createAsyncThunk(
    'elearning/close',
    async (args, { getState }) => {
        const { command } = args || {};
        const { elearning, env, auth } = getState();
        const { launchId } = elearning;

        let data = {};
        switch (command) {
            case 'notify':
                data = await httpPost(`${env.settings.compositionApi}/elearning/session/${launchId}/end`, auth.user);
                break;
        }

        return {
            launchType: 'popup',
            ...data,
        };
    },
);

const getReturnUrl = (launchType, legacy) => {
    const port = window.location.port ? `:${window.location.port}` : '';
    const baseUrl = `${window.location.protocol}//${window.location.hostname}${port}`;

    switch (launchType) {
        case 'self': return window.location.href;
        case 'popup': return legacy
            ? `${baseUrl}/elearning-close-dialog`
            : `${baseUrl}/elearning-close-dialog-and-notify`;
        default: return '';
    }
};

const elearningSlice = createSlice({
    name: 'elearning',
    initialState: {
        status: 'idle',
        error: null
    },
    extraReducers: (builder) => {
        builder
            .addCase(launchElearning.pending, (state, action) => {
                state.launchId = action.meta?.arg?.item?.launchId;
                state.status = 'loading';
            })
            .addCase(launchElearning.fulfilled, (state, action) => {
                const { playerType, launchId, launchType, launchData, url, popup, popupCanClose } = action.payload;

                state.status = 'success';
                state.playerType = playerType;
                state.launchId = launchId;
                state.launchType = launchType;
                state.launchData = launchData;
                state.launchUrl = url;
                state.popupOpen = popup ? true : undefined;
                state.autoClose = popup ? popupCanClose : undefined;
            })
            .addCase(closeElearning.pending, (state) => {
                state.status = 'closing';
            })
            .addCase(closeElearning.fulfilled, (state) => {
                state.status = 'success';
                state.launchId = null;
                state.launchType = launchTypes.LAUNCHTYPE_REGULAR;
                state.launchData = null;
                state.launchUrl = null;
                state.popupOpen = false;
            });
    },
});

export default elearningSlice.reducer;
